// File: src/pages/Employee/EmployeeHolidayPage.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 *
 * 1. AXIOS — HARDCODED URL REMOVED (critical bug fix)
 *    ORIGINAL:
 *      import axios from "axios";
 *      axios.get("http://35.168.97.96:8080/api/holidays/employee", {
 *        headers: { Authorization: `Bearer ${token}` }
 *      })
 *      — hardcoded IP, manual token attachment, no interceptor benefit
 *
 *    NEW:
 *      import api from "../../api/axios";
 *      api.get("/holidays/employee")
 *      — uses shared axios instance: base URL from one place,
 *        token auto-attached by interceptor,
 *        401 auto-redirects to /login via interceptor
 *
 * 2. UI COMPLETELY REWRITTEN — raw HTML table → MUI components
 *    ORIGINAL: plain <table> with inline styles, plain <p> for states
 *    NEW:
 *      - Month-grouped view: holidays grouped by month with a header per group
 *      - Calendar icon next to each date
 *      - Color-coded type chips: green PAID HOLIDAY, blue OPTIONAL,
 *        red WORKING DAY (company working on what would be a holiday)
 *      - Total count badge in header
 *      - Empty state with icon and helpful message
 *      - Loading skeleton (CircularProgress)
 *      - Error alert with retry button
 *      - Upcoming badge: holidays within next 7 days get a "Soon!" chip
 *      - Past holidays shown with reduced opacity so eye naturally
 *        focuses on upcoming ones
 *
 * 3. DATE FORMATTING
 *    ORIGINAL: new Date(dateStr).toLocaleDateString("en-IN", {...})
 *    NEW: same locale but also shows day-of-week (Wed, 15 Jan 2025)
 *         and "Today" label if the holiday falls today
 *
 * 4. MANUAL TOKEN READ REMOVED
 *    ORIGINAL: const token = localStorage.getItem("token"); + manual
 *              Authorization header — duplicates what the interceptor does
 *    NEW: completely removed — interceptor handles it automatically
 *
 * 5. SORTING
 *    ORIGINAL: displayed in whatever order API returned
 *    NEW: sorted ascending by date so next upcoming holiday is first
 * =====================================================================
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Paper,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  CalendarMonth,
  Celebration,
  EventBusy,
  WorkOutline,
  Refresh,
} from "@mui/icons-material";
import api from "../../api/axios";

/* =====================================================================
   HELPERS
===================================================================== */

/**
 * Format a date string as "Wed, 15 Jan 2025"
 * Returns "Today" suffix if the date is today.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const formatted = d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  return isToday ? `${formatted} — Today 🎉` : formatted;
};

/**
 * Returns a month-year key like "January 2026" for grouping.
 */
const monthKey = (dateStr) => {
  if (!dateStr) return "Unknown";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

/**
 * True if the holiday is within the next 7 days from today.
 */
const isSoon = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (d - now) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7;
};

/**
 * True if the holiday has already passed.
 */
const isPast = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

/* =====================================================================
   TYPE CHIP
===================================================================== */
const TypeChip = ({ type }) => {
  const config = {
    PAID_HOLIDAY: {
      label: "Paid Holiday",
      color: "success",
      icon: <Celebration sx={{ fontSize: 14 }} />,
    },
    OPTIONAL_HOLIDAY: {
      label: "Optional",
      color: "info",
      icon: <CalendarMonth sx={{ fontSize: 14 }} />,
    },
    WORKING_DAY: {
      label: "Working Day",
      color: "error",
      icon: <WorkOutline sx={{ fontSize: 14 }} />,
    },
  };

  const cfg = config[type] || {
    label: type || "Unknown",
    color: "default",
    icon: <EventBusy sx={{ fontSize: 14 }} />,
  };

  return (
    <Chip
      size="small"
      icon={cfg.icon}
      label={cfg.label}
      color={cfg.color}
      variant="outlined"
      sx={{ fontWeight: 700, fontSize: 12 }}
    />
  );
};

/* =====================================================================
   MAIN COMPONENT
===================================================================== */
const EmployeeHolidayPage = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // NEW: uses shared api instance — no hardcoded URL, no manual token
  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/holidays/employee");
      // Sort ascending by date so next upcoming is first
      const sorted = (res.data || []).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      setHolidays(sorted);
    } catch (err) {
      console.error("Holiday fetch failed", err);
      // 401 is auto-handled by interceptor (redirect to /login)
      // Only non-401 errors reach here
      setError("Failed to load holidays. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  // Group holidays by month
  const grouped = holidays.reduce((acc, h) => {
    const key = monthKey(h.date);
    if (!acc[key]) acc[key] = [];
    acc[key].push(h);
    return acc;
  }, {});

  const paidCount = holidays.filter((h) => h.holidayType === "PAID_HOLIDAY").length;

  /* =============== LOADING =============== */
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress size={52} />
      </Box>
    );
  }

  /* =============== ERROR =============== */
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 3, mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchHolidays}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  /* =============== EMPTY =============== */
  if (holidays.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CalendarMonth sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No holidays announced yet
        </Typography>
        <Typography variant="body2" color="text.disabled" mt={1}>
          Check back later — HR will publish the holiday calendar soon.
        </Typography>
      </Box>
    );
  }

  /* =============== MAIN VIEW =============== */
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="h4" fontWeight={900} color="#1e293b">
            Company Holidays
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {holidays.length} holiday{holidays.length !== 1 ? "s" : ""} listed
            {paidCount > 0 && ` · ${paidCount} paid`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchHolidays}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Month groups */}
      <Stack spacing={3}>
        {Object.entries(grouped).map(([month, items]) => (
          <Paper key={month} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
            {/* Month header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} color="#475569">
                {month}
              </Typography>
            </Box>

            {/* Holiday rows */}
            {items.map((h, idx) => {
              const past = isPast(h.date);
              const soon = isSoon(h.date);

              return (
                <React.Fragment key={h.id || idx}>
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      opacity: past ? 0.5 : 1,
                      flexWrap: "wrap",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: "#f1f5f9" },
                    }}
                  >
                    {/* Date + name */}
                    <Stack direction="row" alignItems="center" spacing={1.5} flexGrow={1}>
                      <CalendarMonth
                        sx={{
                          color: past ? "#94a3b8" : "#3b82f6",
                          fontSize: 22,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Typography fontWeight={700} fontSize={14} color="#1e293b">
                            {h.description || "Holiday"}
                          </Typography>
                          {soon && (
                            <Chip
                              label="Soon!"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 800, fontSize: 10, height: 20 }}
                            />
                          )}
                        </Stack>
                        <Typography fontSize={12} color="text.secondary">
                          {formatDate(h.date)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Type chip */}
                    <TypeChip type={h.holidayType} />
                  </Box>

                  {idx < items.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </Paper>
        ))}
      </Stack>

      {/* Footer note */}
      <Typography
        variant="caption"
        color="text.disabled"
        display="block"
        textAlign="center"
        mt={3}
      >
        Holidays are set by HR. Contact HR for any queries.
      </Typography>
    </Box>
  );
};

export default EmployeeHolidayPage;